import {Component, Input, OnDestroy, OnInit} from '@angular/core';
import {
  AangifteSchenkbelasting,
  APV, BokkzAlgDataService, BokkzSbDataService, BokkzSbLogic,
  Effecten,
  GiftTaxEntity,
  NotarieleAkte,
  OmschrijvingEnBedrag,
  OndernemingSB,
  OnroerendeZaakSB,
  PU,
  PUUitkeringsPeriode,
  RoerendeZaak,
  Schenking,
  SchenkingOpPapier,
  SchenkingRelatieBegiftigdeMetSchenker,
  Vrijstellingen,
  Vruchtgebruik
} from "@snelstart-ventures/fiscaal/domain-logic";
import {FormBuilder, FormGroup} from "@angular/forms";
import {SelectFormControlItem, SharedUiFormControlsModule} from "@snelstart/ui-form-controls";
import {SharedUiDetailsModule} from "@snelstart/ui-details";
import {NgIf} from "@angular/common";
import {Subscription} from "rxjs";

@Component({
  selector: 'snelstart-venture-fiscaal-feature-uitkeringen',
  standalone: true,
  imports: [
    SharedUiDetailsModule,
    SharedUiFormControlsModule,
    NgIf
  ],
  templateUrl: './uitkeringen.component.html',
  styleUrl: './uitkeringen.component.css'
})
export class UitkeringenComponent implements OnInit, OnDestroy {

  @Input() public id!: string;
  private giftTaxEntitySubscription: Subscription | undefined;
  public giftTaxEntity = {} as GiftTaxEntity;
  public sbAangifte = {} as AangifteSchenkbelasting;
  public schenkingFg = {} as FormGroup;
  public puFg = {} as FormGroup;
  public focusedElementId: string | null = null;

  public optionsJaNee: SelectFormControlItem<boolean>[] = [
    {value: true, label: "Ja"},
    {value: false, label: "Nee"},
  ];

  public optionsPeriode: SelectFormControlItem<string>[] = [
    {value: "JAAR", label: "Jaarlijkse uitkeringen"},
    {value: "KWARTAAL", label: "Kwartaaluitkeringen"},
    {value: "MAAND", label: "Maandelijkse uitkeringen"},
    {value: "WEEK", label: "Wekelijkse uitkeringen"},
  ];

  constructor(
    public algDataService: BokkzAlgDataService,
    public dataService: BokkzSbDataService,
    public sbService: BokkzSbLogic,
    private fb: FormBuilder) {
    this.schenkingFg = this.fb.group<Schenking>({
      datumSchenking: null as unknown as Date,
      relatieBegiftigdeMetSchenker: null as unknown as SchenkingRelatieBegiftigdeMetSchenker,
      herroepelijkeSchenking: null as unknown as boolean,
      schenkerBetaaltDeSchenkbelasting: null as unknown as boolean,
      uitbreidingBegripSchenker: null as unknown as boolean,
      contanten: null as unknown as OmschrijvingEnBedrag,
      effecten: null as unknown as Effecten,
      onroerendeZaken: null as unknown as OnroerendeZaakSB,
      ondernemingen: null as unknown as OndernemingSB,
      apv: null as unknown as APV,
      periodiekeUitkering: null as unknown as PU,
      roerendeZaak: null as unknown as RoerendeZaak,
      schuldigerkenning: null as unknown as SchenkingOpPapier,
      kwijtscheldingSchuld: null as unknown as OmschrijvingEnBedrag,
      vruchtgebruik: null as unknown as Vruchtgebruik,
      overige: null as unknown as OmschrijvingEnBedrag,
      vrijstellingen: null as unknown as Vrijstellingen,
      notarieleAkte: null as unknown as NotarieleAkte,
      belastingBetaaldInAnderLand: null as unknown as number,
      landCodeBetaaldInAnderLand: null as unknown as string,
    })
    this.puFg = this.fb.group<PU>({
      uitkeringsPeriode: null as unknown as PUUitkeringsPeriode,
      uitkeringsbedrag: null as unknown as number,
      datumEersteUitkering: null as unknown as Date,
      datumLaatsteUitkering: null as unknown as Date,
      afhankelijkVanLeven: null as unknown as boolean,
      inkomstenBelastingGeheven: null as unknown as boolean,
      bsnEersteVanLevenAfhankelijke: null as unknown as string,
      geboorteDatumEersteVanLevenAfhankelijke: null as unknown as Date,
      bsnTweedeVanLevenAfhankelijke: null as unknown as string,
      geboorteDatumTweedeVanLevenAfhankelijke: null as unknown as Date,
    })
  }

  ngOnInit(): void {
    this.giftTaxEntitySubscription = this.dataService.giftTaxEntity.subscribe((value)=> {
      this.giftTaxEntity = value;
      this.sbAangifte = value.aangifte!;
      if (value.aangifte!.schenking) {
        this.schenkingFg.patchValue(value.aangifte!.schenking[0], {emitEvent: false})
        if (value.aangifte!.schenking[0] && value.aangifte!.schenking[0].periodiekeUitkering) {
          this.puFg.patchValue(value.aangifte!.schenking[0].periodiekeUitkering, {emitEvent: false})
        }
      }
    })
  }

  ngOnDestroy(): void {
    if (this.giftTaxEntitySubscription) {
      this.giftTaxEntitySubscription.unsubscribe()
    }
  }

  saveForm(): void {
    this.puFg.updateValueAndValidity();
    this.schenkingFg.updateValueAndValidity();
    const schenking = this.schenkingFg.value;
    schenking.periodiekeUitkering = {
      uitkeringsPeriode: this.puFg.value.uitkeringsPeriode,
      uitkeringsbedrag: this.puFg.value.uitkeringsbedrag,
      datumEersteUitkering: this.puFg.value.datumEersteUitkering,
      datumLaatsteUitkering: this.puFg.value.datumLaatsteUitkering,
      afhankelijkVanLeven: this.puFg.value.afhankelijkVanLeven,
      inkomstenBelastingGeheven: this.puFg.value.inkomstenBelastingGeheven,
      bsnEersteVanLevenAfhankelijke: this.puFg.value.bsnEersteVanLevenAfhankelijke,
      geboorteDatumEersteVanLevenAfhankelijke: this.puFg.value.geboorteDatumEersteVanLevenAfhankelijke,
      bsnTweedeVanLevenAfhankelijke: this.puFg.value.bsnTweedeVanLevenAfhankelijke,
      geboorteDatumTweedeVanLevenAfhankelijke: this.puFg.value.geboorteDatumTweedeVanLevenAfhankelijke,
    }
    this.giftTaxEntity.aangifte = {
      ...this.sbAangifte,
      schenking: [schenking],
    } as AangifteSchenkbelasting;
    this.dataService.updateGiftTaxEntity(this.giftTaxEntity);
    setTimeout(()=> {
      if (schenking.periodiekeUitkering.uitkeringsPeriode != undefined
        && schenking.periodiekeUitkering.uitkeringsbedrag != undefined
        && schenking.periodiekeUitkering.datumEersteUitkering != undefined)
      this.sbService.getCalculation();
    }, 1000)
  }
}
